import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { track } from "../../lib/analytics";
import { useTranslation } from "react-i18next";
import { api } from "../../lib/api";
import { SMS_TOPUP_PACKAGES_CENTS, SMS_COST_CENTS } from "@petloc/shared";

interface Ledger {
  balanceCents: number;
  entries: {
    id: string;
    deltaCents: number;
    reason: string;
    createdAt: string;
  }[];
}

export function SmsPage() {
  const { t } = useTranslation();
  const q = useQuery({ queryKey: ["sms"], queryFn: () => api<Ledger>("/api/sms/ledger") });
  const location = useLocation();
  useEffect(() => {
    if (location.pathname.endsWith("/success")) track("topup_paid");
  }, [location.pathname]);

  const topup = async (amountCents: number) => {
    const res = await api<{ url: string }>("/api/sms/topup", {
      method: "POST",
      body: JSON.stringify({ amountCents }),
    });
    window.location.href = res.url;
  };

  return (
    <div className="max-w-2xl">
      <h1 className="font-display text-3xl font-semibold tracking-tight mb-6">{t("sms.balance")}</h1>

      <div className="card p-6 mb-6">
        <div className="text-4xl font-bold">
          {((q.data?.balanceCents ?? 0) / 100).toFixed(2)} €
        </div>
        <div className="text-sm text-slate-500 mt-1">
          ≈ {Math.floor((q.data?.balanceCents ?? 0) / SMS_COST_CENTS)} SMS verfügbar ·{" "}
          {(SMS_COST_CENTS / 100).toFixed(2)} € {t("sms.perSms")}
        </div>

        <div className="mt-6">
          <div className="label">{t("sms.topup")}</div>
          <div className="grid grid-cols-3 gap-3">
            {SMS_TOPUP_PACKAGES_CENTS.map((cents) => (
              <button key={cents} className="btn-secondary" onClick={() => topup(cents)}>
                +{(cents / 100).toFixed(2)} €
              </button>
            ))}
          </div>
          <p className="text-xs text-slate-500 mt-2">{t("sms.topupHint")}</p>
        </div>
      </div>

      <div className="card p-6">
        <div className="label">Bewegungen</div>
        <div className="divide-y divide-slate-100">
          {q.data?.entries.map((e) => (
            <div key={e.id} className="py-2 flex justify-between text-sm">
              <div>
                <div className="text-slate-700">{e.reason}</div>
                <div className="text-xs text-slate-500">{new Date(e.createdAt).toLocaleString()}</div>
              </div>
              <div className={e.deltaCents >= 0 ? "text-green-600" : "text-slate-900"}>
                {e.deltaCents >= 0 ? "+" : ""}
                {(e.deltaCents / 100).toFixed(2)} €
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
